import { motion } from 'framer-motion';
import {
  ArrowRight,
  Award,
  BarChart3,
  Briefcase,
  Building2,
  Calculator,
  CheckCircle,
  FileText,
  GraduationCap,
  Heart,
  Home,
  Shield,
  Target,
  TrendingUp,
  Umbrella,
  Users,
  Zap,
} from 'lucide-react';

import { Footer } from '@components/Footer';
import { Header } from '@components/Header';
import { SEOHead } from '@components/SEOHead';

import { Button } from '@ui/Button';
import { Hero } from '@ui/Hero';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

export const ServicesPage = () => {
  // Main services offered
  const mainServices = [
    {
      icon: Briefcase,
      title: 'Mutual fund investment',
      description:
        'Expert guidance on selecting the right mutual funds based on your goals and risk profile',
      features: [
        'SIP planning & setup',
        'Lumpsum investment strategy',
        'Portfolio diversification',
        'Fund performance analysis',
        'Regular portfolio review',
      ],
      color: COLORS.accent,
      delay: 0.6,
    },
    {
      icon: Target,
      title: 'Goal-based financial planning',
      description:
        'Comprehensive financial planning for your life goals with clear timelines and strategies',
      features: [
        'Retirement planning',
        'Child education fund',
        'Home purchase planning',
        'Emergency fund creation',
        'Wealth accumulation strategy',
      ],
      color: COLORS.sage,
      delay: 0.8,
    },
    {
      icon: Calculator,
      title: 'Tax planning & optimization',
      description:
        'Maximize your tax savings through smart investment choices under various sections',
      features: [
        '80C investment planning',
        'ELSS fund selection',
        'Tax-saving strategies',
        'New vs old tax regime analysis',
        'Annual tax review',
      ],
      color: COLORS.gold,
      delay: 1.0,
    },
    {
      icon: BarChart3,
      title: 'Portfolio management',
      description:
        'Regular monitoring and rebalancing of your investment portfolio for optimal returns',
      features: [
        'Portfolio health check',
        'Asset rebalancing',
        'Performance tracking',
        'Risk assessment',
        'Market updates & insights',
      ],
      color: COLORS.primary,
      delay: 1.2,
    },
  ];

  // Specialized services
  const specializedServices = [
    {
      icon: Home,
      title: 'Home purchase planning',
      description:
        'Strategic planning to help you buy your dream home with the right investment mix',
      timeline: '3-10 years',
      color: COLORS.accent,
    },
    {
      icon: GraduationCap,
      title: 'Child education planning',
      description:
        "Secure your child's educational future with inflation-beating investment strategies",
      timeline: '5-20 years',
      color: COLORS.sage,
    },
    {
      icon: Umbrella,
      title: 'Retirement planning',
      description:
        'Build a substantial retirement corpus for a comfortable and worry-free retirement',
      timeline: '10-30 years',
      color: COLORS.gold,
    },
    {
      icon: Building2,
      title: 'Wealth creation',
      description:
        'Long-term wealth building strategies for financial independence and legacy creation',
      timeline: '10+ years',
      color: COLORS.primary,
    },
  ];

  // Service process
  const serviceProcess = [
    {
      icon: Users,
      title: 'Free consultation',
      description:
        'We start with understanding your financial goals, current situation, and risk tolerance through a detailed discussion.',
      step: '1',
    },
    {
      icon: FileText,
      title: 'Personalized plan',
      description:
        'Based on your goals, we create a customized investment plan with specific fund recommendations and timelines.',
      step: '2',
    },
    {
      icon: Zap,
      title: 'Easy implementation',
      description:
        'We help you with KYC, e-mandates, and all paperwork to get your investments started smoothly.',
      step: '3',
    },
    {
      icon: TrendingUp,
      title: 'Ongoing support',
      description:
        'Regular portfolio reviews, rebalancing, and continuous guidance to keep you on track towards your goals.',
      step: '4',
    },
  ];

  // Why choose our services
  const whyChooseUs = [
    {
      icon: Shield,
      title: 'AMFI certified expertise',
      description:
        'Certified mutual fund distributor with deep knowledge of market dynamics and fund selection',
    },
    {
      icon: Heart,
      title: 'Client-first approach',
      description:
        'Your financial success is our priority. We provide unbiased advice aligned with your best interests',
    },
    {
      icon: Award,
      title: 'Transparent & honest',
      description:
        'No hidden charges, clear explanations, and complete transparency in all our recommendations',
    },
    {
      icon: Users,
      title: 'Personalized service',
      description:
        'Every client gets individual attention with customized solutions based on their unique needs',
    },
  ];

  return (
    <>
      <SEOHead
        title={`Our Services | ${CONTACT_INFO.company.name}`}
        description="Comprehensive financial services including mutual fund investment, goal-based planning, tax optimization, and portfolio management. AMFI certified expert guidance for your wealth building journey."
        keywords="mutual fund investment, financial planning services, goal based planning, tax planning, portfolio management, SIP planning, retirement planning, wealth management"
        canonicalUrl="https://niveshright.com/services"
      />

      <div className="min-h-screen" style={{ backgroundColor: COLORS.pearl }}>
        <Header />

        <Hero
          title={
            <span className="flex items-center justify-center gap-2">
              Our <span style={{ color: COLORS.gold }}>services</span>
            </span>
          }
          description="Comprehensive financial solutions designed to help you achieve your life goals through smart investing and strategic planning"
          variant="gradient"
        >
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div
                className="text-3xl font-bold"
                style={{ color: COLORS.gold }}
              >
                AMFI
              </div>
              <div className="text-white/70">certified services</div>
            </div>
            <div className="text-center">
              <div
                className="text-3xl font-bold"
                style={{ color: COLORS.sage }}
              >
                Goal-based
              </div>
              <div className="text-white/70">planning approach</div>
            </div>
            <div className="text-center">
              <div
                className="text-3xl font-bold"
                style={{ color: COLORS.accent }}
              >
                Personalized
              </div>
              <div className="text-white/70">investment strategy</div>
            </div>
          </div>
        </Hero>

        {/* Main Services Section */}
        <motion.section
          className="py-20"
          style={{ backgroundColor: COLORS.pearl }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2
                className="text-4xl font-bold mb-4"
                style={{ color: COLORS.primary }}
              >
                Our core services
              </h2>
              <p className="text-xl" style={{ color: COLORS.smoke }}>
                Everything you need to build and manage your wealth effectively
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {mainServices.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <motion.div
                    key={service.title}
                    className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-500"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: service.delay }}
                    whileHover={{ y: -8 }}
                  >
                    <div className="flex items-center mb-4">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center mr-4"
                        style={{ backgroundColor: `${service.color}15` }}
                      >
                        <IconComponent
                          size={24}
                          style={{ color: service.color }}
                        />
                      </div>
                      <h3
                        className="text-2xl font-bold"
                        style={{ color: COLORS.primary }}
                      >
                        {service.title}
                      </h3>
                    </div>

                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    <div className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <div
                          key={idx}
                          className="flex items-center text-sm text-gray-700"
                        >
                          <CheckCircle
                            size={16}
                            className="mr-3 flex-shrink-0"
                            style={{ color: service.color }}
                          />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.section>

        {/* Specialized Services */}
        <motion.section
          className="py-20"
          style={{
            background: `linear-gradient(135deg, ${COLORS.secondary} 0%, ${COLORS.primary} 100%)`,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.4 }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2
                className="text-4xl font-bold mb-4"
                style={{ color: COLORS.pearl }}
              >
                Specialized planning services
              </h2>
              <p className="text-xl text-white/80">
                Tailored solutions for your specific life goals
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {specializedServices.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <motion.div
                    key={service.title}
                    className="group relative bg-white rounded-3xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-500 overflow-hidden"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1.6 + index * 0.1 }}
                    whileHover={{ y: -8 }}
                  >
                    {/* Background decoration */}
                    <div
                      className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-5 -mr-16 -mt-16"
                      style={{ backgroundColor: service.color }}
                    />

                    <div className="relative">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center mr-3 shadow-lg group-hover:scale-110 transition-transform duration-300"
                            style={{ backgroundColor: `${service.color}15` }}
                          >
                            <IconComponent
                              size={24}
                              style={{ color: service.color }}
                            />
                          </div>
                          <h3
                            className="text-xl font-bold"
                            style={{ color: COLORS.primary }}
                          >
                            {service.title}
                          </h3>
                        </div>
                        <div
                          className="px-3 py-1 rounded-full text-xs font-semibold"
                          style={{
                            backgroundColor: `${service.color}15`,
                            color: service.color,
                          }}
                        >
                          {service.timeline}
                        </div>
                      </div>

                      <p className="text-gray-600 leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.section>

        {/* Service Process */}
        <motion.section
          className="py-20"
          style={{ backgroundColor: COLORS.mist }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 2.0 }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2
                className="text-4xl font-bold mb-4"
                style={{ color: COLORS.primary }}
              >
                How we work with you
              </h2>
              <p className="text-xl" style={{ color: COLORS.smoke }}>
                A simple, transparent process that puts you in control
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              {serviceProcess.map((step, index) => {
                const IconComponent = step.icon;
                const isLast = index === serviceProcess.length - 1;
                return (
                  <motion.div
                    key={step.title}
                    className="relative flex items-start mb-12 last:mb-0"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 2.2 + index * 0.2 }}
                  >
                    {/* Timeline line */}
                    {!isLast && (
                      <div
                        className="absolute left-8 top-16 w-0.5 h-16 -ml-px"
                        style={{ backgroundColor: `${COLORS.accent}30` }}
                      />
                    )}

                    {/* Step circle with icon */}
                    <div className="flex-shrink-0 relative">
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg border-4 border-white"
                        style={{ backgroundColor: COLORS.accent }}
                      >
                        <IconComponent size={24} color="white" />
                      </div>
                      <div
                        className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md"
                        style={{ backgroundColor: COLORS.sage }}
                      >
                        {step.step}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="ml-8 flex-1">
                      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                        <h3
                          className="text-xl font-bold mb-3"
                          style={{ color: COLORS.primary }}
                        >
                          {step.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.section>

        {/* Why Choose Us */}
        <motion.section
          className="py-20"
          style={{
            background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 2.6 }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2
                className="text-4xl font-bold mb-4"
                style={{ color: COLORS.pearl }}
              >
                Why choose us?
              </h2>
              <p className="text-xl text-white/80">
                The advantages of working with {CONTACT_INFO.company.name}
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {whyChooseUs.map((reason, index) => {
                  const IconComponent = reason.icon;
                  return (
                    <motion.div
                      key={reason.title}
                      className="flex items-start space-x-4 p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all duration-300"
                      initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 2.8 + index * 0.1 }}
                    >
                      <div className="flex-shrink-0">
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${COLORS.gold}25` }}
                        >
                          <IconComponent
                            size={24}
                            style={{ color: COLORS.gold }}
                          />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold mb-2 text-white">
                          {reason.title}
                        </h3>
                        <p className="text-white/80 text-sm leading-relaxed">
                          {reason.description}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          className="py-20"
          style={{ backgroundColor: COLORS.pearl }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 3.2 }}
        >
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2
              className="text-4xl font-bold mb-6"
              style={{ color: COLORS.primary }}
            >
              Ready to start your investment journey?
            </h2>
            <p className="text-xl mb-8" style={{ color: COLORS.smoke }}>
              Book a free consultation with{' '}
              {CONTACT_INFO.contact_person.short_name} and discover how to turn
              your financial goals into reality through smart investing.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <Button
                href={`https://wa.me/${CONTACT_INFO.phone.whatsapp}?text=Hi ${CONTACT_INFO.contact_person.short_name}, I'm interested in your financial planning services and would like to schedule a free consultation.`}
                target="_blank"
                rel="noopener noreferrer"
                variant="sage"
                size="lg"
                icon={ArrowRight}
                iconSize={20}
              >
                Book free consultation
              </Button>

              <Button
                href="/tools"
                variant="outline"
                size="lg"
                icon={Calculator}
                iconSize={20}
              >
                Try our free financial tools
              </Button>
            </div>

            <div className="flex items-center justify-center space-x-6 text-gray-600 text-sm">
              <div className="flex items-center space-x-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: COLORS.sage }}
                ></div>
                <span>Free consultation</span>
              </div>
              <div className="flex items-center space-x-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: COLORS.accent }}
                ></div>
                <span>No hidden charges</span>
              </div>
              <div className="flex items-center space-x-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: COLORS.gold }}
                ></div>
                <span>AMFI certified</span>
              </div>
            </div>
          </div>
        </motion.section>

        <Footer />
      </div>
    </>
  );
};
